import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { feedPageQueryOptions } from "../queries/deals";
import { Feed } from "../components/feed";
import { FeedControls, type FeedControlValue } from "../components/feed-controls";
import { addRecentSearch } from "../lib/recent-searches";

const SearchSchema = z.object({
  q: z.string().optional(),
  category: z.string().optional(),
  tag: z.string().optional(),
  merchant: z.string().optional(),
  sort: z.enum(["hot", "new"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/search")({
  validateSearch: SearchSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps }) => {
    const s = deps.search;
    void context.queryClient.prefetchQuery(
      feedPageQueryOptions({
        sort: s.sort ?? "hot",
        category: s.category,
        tag: s.tag,
        merchant: s.merchant,
        q: s.q,
        page: s.page ?? 1,
      }),
    );
  },
  component: SearchPage,
});

function SearchPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const page = search.page ?? 1;

  useEffect(() => {
    if (search.q) addRecentSearch(search.q);
  }, [search.q]);

  function apply(next: FeedControlValue) {
    navigate({
      to: "/search",
      search: {
        ...search,
        q: next.q,
        category: next.category,
        merchant: next.merchant,
        sort: next.sort === "new" ? "new" : undefined,
        page: undefined,
      },
    });
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 sm:px-5 py-6 sm:py-8 flex-1">
      <p className="text-xs text-[color:var(--color-fg-muted)] mb-2">
        <Link to="/" className="hover:underline">← back</Link>
      </p>
      <FeedControls
        value={{ q: search.q, category: search.category, merchant: search.merchant, sort: search.sort ?? "hot" }}
        onApply={apply}
      />
      <Feed
        sort={search.sort ?? "hot"}
        category={search.category}
        tag={search.tag}
        merchant={search.merchant}
        q={search.q}
        page={page}
        onPage={(p) => navigate({ to: "/search", search: { ...search, page: p <= 1 ? undefined : p } })}
      />
    </main>
  );
}
